import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export function FeedbackForm({ orderId, onSubmitted }) {
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [clean, setClean] = useState(0);
  const [delivery, setDelivery] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!food || !service || !clean) { toast.error('Please rate food, service and cleanliness'); return; }
    setSubmitting(true);
    try {
      await api.post('/me/feedback', {
        order_id: orderId,
        food_rating: food,
        service_rating: service,
        cleanliness_rating: clean,
        delivery_rating: delivery || null,
        comment: comment || null,
      });
      setDone(true);
      toast.success('Thanks for your feedback!');
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSubmitting(false); }
  };

  if (done) return (
    <Card><CardContent className="p-6 text-center text-sm"><span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">✓</span><p className="mt-2 font-display text-lg font-semibold">Thank you!</p><p className="text-muted-foreground">Your feedback helps us serve you better.</p></CardContent></Card>
  );

  return (
    <Card>
      <CardHeader><CardTitle>Rate your experience</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <StarRow label="Food" value={food} onChange={setFood} testid="feedback-food" />
        <StarRow label="Service" value={service} onChange={setService} testid="feedback-service" />
        <StarRow label="Cleanliness" value={clean} onChange={setClean} testid="feedback-cleanliness" />
        <StarRow label="Delivery (optional)" value={delivery} onChange={setDelivery} testid="feedback-delivery" />
        <div className="space-y-1.5"><Label>Comment (optional)</Label><Textarea data-testid="feedback-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What did you enjoy? Anything we can do better?" /></div>
        <Button data-testid="feedback-submit-button" onClick={submit} disabled={submitting || !food || !service || !clean}><Send className="mr-1 h-4 w-4" /> {submitting ? 'Sending…' : 'Submit feedback'}</Button>
      </CardContent>
    </Card>
  );
}

function StarRow({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`${testid}-star-${n}`} onClick={() => onChange(n)} className="p-1">
            <Star className={`h-7 w-7 transition-colors ${n <= value ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground/30 hover:text-amber-500'}`} />
          </button>
        ))}
        <span className="ml-2 text-sm tabular text-muted-foreground">{value || 0}/5</span>
      </div>
    </div>
  );
}
